import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Link, useLocation, useNavigate } from "@tanstack/solid-router";
import { Filter, Pencil, Plus } from "@fleet/icons";
import { cn, getSavedViewsQuery, queryKeys } from "@listto/shared";
import { createPowerSyncQuery } from "@listto/shared/solid";
import type { SavedView } from "@listto/powersync";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { SavedViewDialog } from "./saved-view-dialog";

const inactiveClass =
  "text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:bg-accent/5 transition-[background-color,color,box-shadow]";
const activeClass = "bg-accent/10 text-accent font-semibold";

interface SavedViewsSectionProps {
  onNavigate?: () => void;
}

/**
 * Sidebar section with user's saved smart views:
 * navigation to `/views/$viewId`, edit pencil per view and creation with "+".
 * The create/edit dialog lives here (local state, GroupDialog pattern).
 */
export function SavedViewsSection(props: SavedViewsSectionProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const navigate = useNavigate();
  const location = useLocation();

  const [dialogOpen, setDialogOpen] = createSignal(false);
  const [editingView, setEditingView] = createSignal<SavedView | null>(null);

  const savedViews = createPowerSyncQuery({
    queryKey: () => queryKeys.savedViews.all(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = userId();
      return dbInstance && uid ? getSavedViewsQuery(dbInstance, uid) : null;
    },
    enabled: () => Boolean(drizzle() && userId()),
  });

  const openCreate = () => {
    setEditingView(null);
    setDialogOpen(true);
  };

  const openEdit = (view: SavedView) => {
    setEditingView(view);
    setDialogOpen(true);
  };

  // If the view whose route is active gets deleted, return to dashboard to avoid
  // leaving the user in a not-found state.
  const handleDeleted = (id: string) => {
    if (location().pathname.includes(`/views/${id}`)) {
      void navigate({ to: "/" });
    }
  };

  return (
    <>
      <div class="mt-4 px-3 mb-1 flex items-center justify-between">
        <h3 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          {t(msg`Saved views`)}
        </h3>
        <button
          type="button"
          onClick={openCreate}
          class="flex h-5 w-5 items-center justify-center rounded text-muted-foreground/70 hover:bg-accent/10 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
          aria-label={t(msg`New saved view`)}
          data-testid="saved-view-add"
        >
          <Plus class="h-3.5 w-3.5" />
        </button>
      </div>

      <Show when={(savedViews.data ?? []).length === 0}>
        <p class="px-3 pb-1 text-xs text-muted-foreground/60">
          {t(msg`Save your favorite filters as views.`)}
        </p>
      </Show>

      <For each={savedViews.data ?? []}>
        {(view) => (
          <div class="group/view relative">
            <Link
              to="/views/$viewId"
              params={{ viewId: view.id }}
              onClick={props.onNavigate}
              class={cn(
                "flex items-center gap-2.5 rounded-md px-3 py-2.5 pr-9 text-sm font-medium transition-[background-color,color,box-shadow]",
                inactiveClass,
              )}
              activeProps={{
                class: cn(
                  "flex items-center gap-2.5 rounded-md px-3 py-2.5 pr-9 text-sm font-medium transition-colors",
                  activeClass,
                ),
              }}
              data-testid="saved-view-link"
            >
              <Filter class="h-4 w-4 shrink-0" />
              <span class="min-w-0 flex-1 truncate">{view.name}</span>
            </Link>
            <button
              type="button"
              onClick={() => {
                openEdit(view);
              }}
              class="absolute right-2 top-1/2 z-10 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded text-muted-foreground/0 group-hover/view:text-muted-foreground/70 focus-visible:text-muted-foreground/70 hover:!text-accent hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
              aria-label={t(msg`Edit view`)}
              data-testid="saved-view-edit"
            >
              <Pencil class="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </For>

      <SavedViewDialog
        open={dialogOpen()}
        onOpenChange={setDialogOpen}
        view={editingView()}
        onDeleted={handleDeleted}
      />
    </>
  );
}
